import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import OrderConfirmation from './components/OrderConfirmation';
import DigitalTicket from './components/DigitalTicket';
import OrderSummary from './components/OrderSummary';
import EventInformation from './components/EventInformation';
import SocialSharing from './components/SocialSharing';
import ActionButtons from './components/ActionButtons';

const OrderSuccessPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);

  // Mock order data - in real app, this would come from URL params or API
  const mockOrderData = {
    orderNumber: "TIX-" + Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase(),
    email: "john.doe@example.com",
    orderDate: new Date()?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    tickets: [
      {
        id: "TKT-" + Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase(),
        eventName: "Summer Music Festival 2024",
        ticketType: "General Admission",
        holderName: "John Doe",
        eventDate: "July 15, 2024",
        eventTime: "6:00 PM",
        venueName: "Central Park Amphitheater",
        venueAddress: "1234 Park Avenue, New York, NY 10001",
        seatInfo: "General Admission - Standing"
      },
      {
        id: "TKT-" + Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase(),
        eventName: "Summer Music Festival 2024",
        ticketType: "General Admission",
        holderName: "Jane Doe",
        eventDate: "July 15, 2024",
        eventTime: "6:00 PM",
        venueName: "Central Park Amphitheater",
        venueAddress: "1234 Park Avenue, New York, NY 10001",
        seatInfo: "General Admission - Standing"
      }
    ],
    orderItems: [
      {
        type: "ticket",
        name: "General Admission Ticket",
        description: "Summer Music Festival 2024",
        quantity: 2,
        unitPrice: 89.99,
        totalPrice: 179.98
      },
      {
        type: "upgrade",
        name: "VIP Parking Pass",
        description: "Premium parking access",
        quantity: 1,
        unitPrice: 25.00,
        totalPrice: 25.00
      },
      {
        type: "merchandise",
        name: "Festival T-Shirt",
        description: "Official festival merchandise - Size L",
        quantity: 1,
        unitPrice: 29.99,
        totalPrice: 29.99
      }
    ],
    totals: {
      subtotal: 234.97,
      serviceFee: 18.75,
      tax: 20.24,
      total: 273.96
    },
    eventDetails: {
      id: "evt-123",
      name: "Summer Music Festival 2024",
      date: "Saturday, July 15, 2024",
      time: "6:00 PM - 11:00 PM",
      timezone: "EST",
      venueName: "Central Park Amphitheater",
      venueAddress: "1234 Park Avenue, New York, NY 10001",
      parkingInfo: "Paid parking available on-site ($15)",
      entryInstructions: "Present QR code at main entrance. Gates open at 5:00 PM.",
      contactPhone: "(555) 123-4567",
      contactEmail: "info@summermusicfest.com",
      specialInstructions: [
        "No outside food or beverages allowed",
        "Bags subject to search at entrance",
        "Event will proceed rain or shine",
        "No re-entry permitted once you leave the venue"
      ]
    }
  };

  useEffect(() => {
    // In a real app, you would fetch order data based on order ID from URL params
    // For now, we'll use mock data
    setOrderData(mockOrderData);

    // Redirect to home if no order data (in real app, this would check for valid order ID)
    const timer = setTimeout(() => {
      if (!orderData && !location?.state?.fromCheckout) {
        // Uncomment the line below in production to redirect users without valid orders
        // navigate('/home');
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [location?.state, navigate, orderData]);

  const handleAddToWallet = (ticketId, device) => {
    // In a real app, this would generate wallet passes
    console.log(`Adding ticket ${ticketId} to ${device} wallet`);
    
    // Mock wallet integration
    if (device === 'ios') {
      // Generate Apple Wallet pass
      alert('Apple Wallet pass would be generated here');
    } else if (device === 'android') {
      // Generate Google Pay pass
      alert('Google Pay pass would be generated here');
    } else {
      // Web-based wallet or download
      alert('Digital wallet pass would be generated here');
    }
  };

  const handleResendEmail = async (orderNumber, email) => {
    // Mock email resend functionality
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log(`Resending confirmation email for order ${orderNumber} to ${email}`);
        resolve();
      }, 1000);
    });
  };

  if (!orderData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your order details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Order Confirmation */}
        <OrderConfirmation
          orderNumber={orderData?.orderNumber}
          email={orderData?.email}
          orderDate={orderData?.orderDate}
        />

        {/* Digital Tickets */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-foreground mb-6">Your Digital Tickets</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {orderData?.tickets?.map((ticket, index) => (
              <DigitalTicket
                key={ticket?.id}
                ticket={ticket}
                onAddToWallet={handleAddToWallet}
              />
            ))}
          </div>
        </div>

        {/* Order Summary and Event Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <OrderSummary
            orderItems={orderData?.orderItems}
            totals={orderData?.totals}
          />
          <EventInformation
            eventDetails={orderData?.eventDetails}
          />
        </div>

        {/* Social Sharing */}
        <div className="mb-8">
          <SocialSharing
            eventDetails={orderData?.eventDetails}
            orderNumber={orderData?.orderNumber}
          />
        </div>

        {/* Action Buttons */}
        <ActionButtons
          orderNumber={orderData?.orderNumber}
          email={orderData?.email}
          onResendEmail={handleResendEmail}
        />
      </div>
    </div>
  );
};

export default OrderSuccessPage;